import {
  SnapshotChannel,
  ObservableStatus,
  acknowledgeWithin,
  SyncRequest,
} from "@client/realtime/snapshot-channel";
import { io, type Socket } from "socket.io-client";

import {
  type ClientToServerEvents,
  type ServerToClientEvents,
  type StateSnapshot,
  type DisplayProjection,
  type SyncAck,
} from "@saboteur/contracts";

type DisplayClientEvents = Pick<ClientToServerEvents, "game:sync">;
type DisplaySocket = Socket<ServerToClientEvents, DisplayClientEvents>;
type DisplaySnapshot = Extract<StateSnapshot, { audience: "display" }>;

export type DisplayConnectionState =
  "connecting" | "connected" | "reconnecting" | "invalid" | "expired" | "error";

export interface DisplaySocketOptions {
  readonly displayCode: string;
  readonly initialVersion?: number;
  readonly socket?: DisplaySocket;
  readonly syncTimeoutMs?: number;
}

export class DisplayGameSocket {
  readonly #socket: DisplaySocket;
  readonly #snapshots: SnapshotChannel<"display">;
  readonly #syncRequest = new SyncRequest();
  readonly #status = new ObservableStatus<DisplayConnectionState>("connecting");
  readonly #syncTimeoutMs: number;

  public constructor(options: DisplaySocketOptions) {
    this.#snapshots = new SnapshotChannel("display", options.initialVersion);
    this.#syncTimeoutMs = options.syncTimeoutMs ?? 5_000;
    this.#socket =
      options.socket ??
      io({
        autoConnect: false,
        auth: { audience: "display", displayCode: options.displayCode },
        reconnection: true,
      });

    this.#socket.on("connect", () => {
      this.setStatus("connected");
      void this.sync();
    });
    this.#socket.on("disconnect", (reason) => {
      this.#syncRequest.invalidate();
      this.setStatus(reason === "io client disconnect" ? "error" : "reconnecting");
    });
    this.#socket.on("connect_error", (error) => {
      const message = error.message.toUpperCase();
      if (message.includes("EXPIRED")) this.setStatus("expired");
      else if (message.includes("NOT_FOUND") || message.includes("UNAUTHORIZED")) {
        this.setStatus("invalid");
      } else this.setStatus("reconnecting");
    });
    this.#socket.on("room:expired", () => this.setStatus("expired"));
    this.#socket.on("presence:update", (presence) => {
      if (!this.#snapshots.mergePresence(presence)) void this.sync();
    });
    this.#socket.on("state:snapshot", (rawSnapshot) => {
      if (this.#snapshots.accept(rawSnapshot) === "gap") void this.sync();
    });
  }

  public connect(): void {
    this.setStatus("connecting");
    this.#socket.connect();
  }

  public disconnect(): void {
    this.#socket.disconnect();
  }

  public subscribe(listener: (snapshot: DisplaySnapshot) => void): () => void {
    return this.#snapshots.subscribe(listener);
  }

  public subscribeStatus(listener: (state: DisplayConnectionState) => void): () => void {
    return this.#status.subscribe(listener);
  }

  public acceptBootstrap(state: DisplayProjection): void {
    this.#snapshots.accept({ audience: "display", version: state.version, state }, true);
  }

  public sync(): Promise<void> {
    return this.#syncRequest.run(async (isCurrent) => {
      const ack = await acknowledgeWithin<SyncAck>(
        (acknowledge) => this.#socket.emit("game:sync", acknowledge),
        this.#syncTimeoutMs,
      );
      if (!isCurrent()) return;
      if (!ack) {
        this.setStatus("reconnecting");
        return;
      }
      if (ack.status === "ok") {
        if (this.#snapshots.accept(ack.snapshot, true) !== "ignored") this.setStatus("connected");
      } else
        this.setStatus(
          ack.code === "ROOM_EXPIRED"
            ? "expired"
            : ack.code === "ROOM_NOT_FOUND"
              ? "invalid"
              : "error",
        );
    });
  }

  private setStatus(status: DisplayConnectionState): void {
    if (status === "invalid" || status === "expired") this.#syncRequest.invalidate();
    this.#status.set(status);
  }
}
